import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChatCircleText, CircleNotch, PaperPlaneRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const STATUS_CHIP = {
  Proposed: "bg-blue-50 text-blue-700 border border-blue-200",
  Approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border border-red-200",
  "On Hold": "bg-amber-50 text-amber-700 border border-amber-300",
};

export const CommentBadge = ({ summary, onClick, entityKey }) => {
  const s = summary || { count: 0, latest_status: null };
  return (
    <button data-testid={`comment-badge-${entityKey}`} onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 border border-slate-200 px-2 py-1 hover:bg-slate-50">
      <ChatCircleText size={12} /> {s.count}
      {s.latest_status && <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold ${STATUS_CHIP[s.latest_status]}`}>{s.latest_status}</span>}
    </button>
  );
};

export const CommentThread = ({ productId, entityType, entityKey, onChanged }) => {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/comments/${productId}/thread`, { params: { entity_type: entityType, entity_key: entityKey } })
      .then((r) => setItems(r.data)).catch(console.error);
  }, [productId, entityType, entityKey]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return toast.error("Write a comment first");
    setBusy(true);
    try {
      await axios.post(`${API}/comments/${productId}`, { entity_type: entityType, entity_key: entityKey, text, status });
      setText(""); setStatus("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add comment");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`comment-thread-${entityKey}`} className="mt-2.5 border border-slate-200 bg-slate-50/50">
      <div className="px-3.5 py-2.5 flex flex-wrap items-center gap-2 border-b border-slate-100">
        <input data-testid={`comment-input-${entityKey}`} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment for the review trail…" className="flex-1 min-w-48 text-[11px] border border-slate-300 bg-white px-2.5 py-1.5" />
        <select data-testid={`comment-status-${entityKey}`} value={status} onChange={(e) => setStatus(e.target.value)}
          className="text-[11px] border border-slate-300 bg-white px-2 py-1.5">
          <option value="">No status change</option>
          {Object.keys(STATUS_CHIP).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid={`comment-submit-${entityKey}`} disabled={busy} onClick={submit}
          className="bg-slate-900 text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <CircleNotch size={11} className="animate-spin" /> : <PaperPlaneRight size={11} weight="bold" />} Post
        </button>
      </div>
      {items === null ? (
        <p className="px-3.5 py-3 text-[11px] text-slate-400">Loading thread…</p>
      ) : items.length === 0 ? (
        <p className="px-3.5 py-3 text-[11px] text-slate-400">No comments yet — start the review trail.</p>
      ) : items.map((c) => (
        <div key={c.id} className="px-3.5 py-2.5 border-b border-slate-100 last:border-0">
          <p className="text-[11px] text-slate-700 leading-relaxed">{c.text}</p>
          <p className="font-mono-data text-[10px] text-slate-400 mt-1">
            {c.author} · {new Date(c.created_at).toLocaleString()}
            {c.status && <span className={`ml-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold ${STATUS_CHIP[c.status]}`}>{c.status}</span>}
          </p>
        </div>
      ))}
    </div>
  );
};
